<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { _battle } from "../ui/battle";
import { DEFAULT_STAGE_VERTICAL_OFFSET_PERCENT, stageCameraCenterForOffsetPercent } from "../ui/stageCamera";
import { Seer } from "../core/seer";
import { Pet } from "../core/pet";
import { RoundStep } from "../core/round";
import { Skill } from "../core/skill";
import { timingLabels } from "../core/timing";
import { ReplayController, shouldPlayHit } from "../ui/replay";

import SkillGroup from "./SkillGroup.vue";
import PetPackage from "./PetPackage.vue";
import PropGroup from "./PropGroup.vue";
import ChoiceGroup from "./ChoiceGroup.vue";
import PetInfo from "./PetInfo.vue";
import BattleInfo from "./BattleInfo.vue";
import SwfAnimation from "./SwfAnimation.vue";
import Restrain from "./Restrain.vue";
import Damage from "./Damage.vue";
import SkillCastBanner from "./SkillCastBanner.vue";
import DraftOverlay from "./DraftOverlay.vue";
import SwfClipAnimation from "./SwfClipAnimation.vue";
import { resetSwfPreload, swfPreloadState, warmSwfCache } from "../ui/swfPreload";
import {
    resetSwfClipPreload,
    retainSwfClipSkins,
    swfClipPreloadState,
    warmSwfClipCache,
} from "../ui/swfClipPreload";
import {
    DEFAULT_ANIMATION_MODE,
    type AnimationMode,
} from "../core/animationMode";
import {
    BATTLE_STAGE_PADDING,
    MAX_BATTLE_STAGE_ZOOM,
    MIN_BATTLE_STAGE_ZOOM,
    DEFAULT_BATTLE_STAGE_ZOOM,
    calculateBattleStageFitScale,
    calculateBattleStageFitZoom,
    clampBattleStageZoom,
    unionBattleStageBounds,
    type BattleStageBounds,
    type BattleStageRenderViewport,
} from "../core/battleStageViewport";
import { SWF_STAGE_HEIGHT, SWF_STAGE_WIDTH } from "../core/swfBattleStage";

const battle = _battle;

/** 当前显示局面（实时执行/回看/选择统一出口） */
const displaySitu = computed(() => battle.displaySituation);
const leftSeer = computed(() => displaySitu.value.leftSeer as Seer);
const rightSeer = computed(() => displaySitu.value.rightSeer as Seer);
const leftPet = computed(() => leftSeer.value.getBattlePet() as Pet);
const rightPet = computed(() => rightSeer.value.getBattlePet() as Pet);

/** 左右双方各自的选项卡状态 */
type OperationTab = "skill" | "bag" | "prop" | "choice";
const leftTab = ref<OperationTab>("skill");
const rightTab = ref<OperationTab>("skill");

// SwfAnimation 组件实例引用
const leftAnim = ref<InstanceType<typeof SwfAnimation> | null>(null);
const rightAnim = ref<InstanceType<typeof SwfAnimation> | null>(null);
const leftSwfClipAnim = ref<InstanceType<typeof SwfClipAnimation> | null>(null);
const rightSwfClipAnim = ref<InstanceType<typeof SwfClipAnimation> | null>(null);
const skillCastBanner = ref<InstanceType<typeof SkillCastBanner> | null>(null);
const animationMode = ref<AnimationMode>(DEFAULT_ANIMATION_MODE);
const showSettings = ref(false);
const showBattleInfo = ref(false);
const portraitHintDismissed = ref(false);
const battleVisible = computed(() => battle.phase !== "draft");
const displayRoundNumber = computed(() => {
    if (battle.viewMode === "review") return battle.viewRound + 1;
    if (battle.phase === "selecting") return battle.rounds.length + 1;
    return battle.rounds.length;
});
const sharedStageMode = computed(() => animationMode.value === "swfclip");

const STAGE_SETTINGS_STORAGE_KEY = "seer-simulator-stage-settings";
const DEFAULT_STAGE_BACKGROUND_COLOR = "#12121f";

function readStageBackgroundColor(): string {
    try {
        const raw = localStorage.getItem(STAGE_SETTINGS_STORAGE_KEY);
        if (!raw) return DEFAULT_STAGE_BACKGROUND_COLOR;
        const value = JSON.parse(raw) as { backgroundColor?: unknown };
        return typeof value.backgroundColor === "string" && /^#[0-9a-f]{6}$/i.test(value.backgroundColor)
            ? value.backgroundColor.toLowerCase()
            : DEFAULT_STAGE_BACKGROUND_COLOR;
    } catch {
        return DEFAULT_STAGE_BACKGROUND_COLOR;
    }
}

const stageViewportRef = ref<HTMLElement | null>(null);
const stageBackgroundColor = ref(readStageBackgroundColor());
const stageViewportWidth = ref(SWF_STAGE_WIDTH);
const stageViewportHeight = ref(SWF_STAGE_HEIGHT);
const leftStageBounds = ref<BattleStageBounds>();
const rightStageBounds = ref<BattleStageBounds>();
// Logical 1x always means the same 960x560 stage scale, independent of the
// current pets' action/effect bounds. Overflow fitting is a separate zoom.
const stageFitScale = ref(1);
const stageZoom = ref(DEFAULT_BATTLE_STAGE_ZOOM);
const stageVerticalOffsetPercent = ref(DEFAULT_STAGE_VERTICAL_OFFSET_PERCENT);
const stageVerticalOffsetInput = ref(String(DEFAULT_STAGE_VERTICAL_OFFSET_PERCENT));
const stageCameraCenter = computed(() =>
    stageCameraCenterForOffsetPercent(stageVerticalOffsetPercent.value),
);

const combinedStageBounds = computed(() =>
    unionBattleStageBounds([leftStageBounds.value, rightStageBounds.value]),
);

const stageRenderViewport = computed<BattleStageRenderViewport>(() => {
    return {
        width: stageViewportWidth.value,
        height: stageViewportHeight.value,
        ...stageCameraCenter.value,
        scale: stageFitScale.value * stageZoom.value,
    };
});

const stageZoomInput = ref(String(Number((stageZoom.value * 100).toFixed(2))));

function syncStageViewportInputs() {
    stageZoomInput.value = String(Number((stageZoom.value * 100).toFixed(2)));
    stageVerticalOffsetInput.value = String(stageVerticalOffsetPercent.value);
}

watch(
    stageZoom,
    syncStageViewportInputs,
);

function updateStageViewportSize() {
    const viewport = stageViewportRef.value;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    stageViewportWidth.value = Math.max(1, rect.width);
    stageViewportHeight.value = Math.max(1, rect.height);
    // Do not derive the base scale from pet bounds: large attack effects must
    // not make the idle pet smaller at the same 1x zoom value.
    stageFitScale.value = calculateBattleStageFitScale(
        { width: rect.width, height: rect.height },
    );
}

function fitStageToWindow(resetZoom = true) {
    updateStageViewportSize();
    if (resetZoom) {
        // Keep the canonical 1x scale stable. Only the explicit fit action
        // lowers the zoom enough to reveal pixels outside that stage.
        stageZoom.value = clampBattleStageZoom(
            calculateBattleStageFitZoom(
                {
                    width: stageViewportWidth.value,
                    height: stageViewportHeight.value,
                },
                combinedStageBounds.value,
                BATTLE_STAGE_PADDING,
                stageCameraCenter.value,
            ),
        );
    }
}

function setStageBounds(side: "left" | "right", bounds: BattleStageBounds) {
    if (side === "left") leftStageBounds.value = bounds;
    else rightStageBounds.value = bounds;
    fitStageToWindow(false);
}

function clearStageBounds(side: "left" | "right") {
    if (side === "left") leftStageBounds.value = undefined;
    else rightStageBounds.value = undefined;
    requestAnimationFrame(() => fitStageToWindow(false));
}

function resetStageBackgroundColor() {
    stageBackgroundColor.value = DEFAULT_STAGE_BACKGROUND_COLOR;
}

function applyStageViewport() {
    const zoom = Number(stageZoomInput.value) / 100;
    const offset = Number(stageVerticalOffsetInput.value);
    if (
        !String(stageZoomInput.value).trim() ||
        !String(stageVerticalOffsetInput.value).trim() ||
        !Number.isFinite(offset) ||
        !Number.isFinite(zoom) ||
        zoom < MIN_BATTLE_STAGE_ZOOM ||
        zoom > MAX_BATTLE_STAGE_ZOOM
    ) {
        syncStageViewportInputs();
        return;
    }
    stageZoom.value = zoom;
    stageVerticalOffsetPercent.value = offset;
    syncStageViewportInputs();
}

let stageResizeObserver: ResizeObserver | null = null;

watch(
    stageViewportRef,
    (viewport) => {
        stageResizeObserver?.disconnect();
        stageResizeObserver = null;
        if (!viewport) return;
        requestAnimationFrame(updateStageViewportSize);
        if (typeof ResizeObserver === "undefined") return;
        stageResizeObserver = new ResizeObserver(updateStageViewportSize);
        stageResizeObserver.observe(viewport);
    },
    { flush: "post" },
);

watch(stageBackgroundColor, (backgroundColor) => {
    try {
        localStorage.setItem(
            STAGE_SETTINGS_STORAGE_KEY,
            JSON.stringify({ backgroundColor }),
        );
    } catch {
        // A restricted browser storage policy should not prevent battle play.
    }
});

watch(animationMode, () => requestAnimationFrame(updateStageViewportSize));

watch(
    () => leftPet.value?.当前皮肤.id,
    () => clearStageBounds("left"),
);

watch(
    () => rightPet.value?.当前皮肤.id,
    () => clearStageBounds("right"),
);

onMounted(() => window.addEventListener("resize", updateStageViewportSize));

onBeforeUnmount(() => {
    stageResizeObserver?.disconnect();
    window.removeEventListener("resize", updateStageViewportSize);
});

const preloadState = computed(() => {
    const state = animationMode.value === "swfclip"
        ? swfClipPreloadState
        : swfPreloadState;
    return {
        label: animationMode.value === "swfclip" ? "SWFClip" : "SWF",
        status: state.status,
        total: state.total,
        finished: state.done + state.failed,
    };
});

// 回放节奏（毫秒）
const SETTLE_MS = 900;
const SWITCH_MS = 400;
const SKIP_MS = 400;

/** 当前步骤提示文案（受阻/回合结束结算） */
const stepMessage = ref("");

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function animationFor(side: "left" | "right") {
    if (animationMode.value === "swfclip") {
        return side === "left" ? leftSwfClipAnim.value : rightSwfClipAnim.value;
    }
    return side === "left" ? leftAnim.value : rightAnim.value;
}

const replay = new ReplayController();
onBeforeUnmount(() => replay.cancel());

async function runReplay() {
    const round = battle.currentRound;
    if (!round) return;
    stepMessage.value = "";
    await replay.run(round.steps, {
        isCurrent: () => battle.phase === "executing" && battle.currentRound === round,
        play: playStep,
        commit: () => battle.advanceStep(),
        settle: () => delay(SETTLE_MS),
        finish: () => {
            stepMessage.value = "";
            battle.finishRound();
        },
    });
}

async function playStep(step: RoundStep) {
    const attackerAnim = step.side ? animationFor(step.side) : null;

    if (step.actionType === "settle") {
        // 回合结束结算步：仅展示提示，等待节拍由回放循环提供
        stepMessage.value = step.timing ? `${timingLabels[step.timing]}结算中…` : "结算中…";
        await delay(SKIP_MS);
        return;
    }
    stepMessage.value = "";

    if (step.actionType === "skill") {
        if (
            step.notExecuted ||
            battle.viewMode !== "live" ||
            !step.skill
        ) {
            if (step.blockReason) {
                stepMessage.value = `【${step.actorName}】因【${step.blockReason}】无法行动`;
            }
            await delay(SKIP_MS);
            return;
        }

        const skill = step.skill;
        const animations: Promise<void>[] = [];
        if (attackerAnim) {
            animations.push(attackerAnim.playAttack(skill));
        }
        if (animationMode.value === "swfclip" && shouldPlayHit(step) && step.side) {
            const attackerClip = step.side === "left"
                ? leftSwfClipAnim.value
                : rightSwfClipAnim.value;
            const defenderClip = step.side === "left"
                ? rightSwfClipAnim.value
                : leftSwfClipAnim.value;
            const hitFrameCount = defenderClip?.getSequenceFrameCount("hited") ?? 0;
            if (attackerClip && defenderClip && hitFrameCount > 0) {
                const leadDurationMs = attackerClip.getAttackLeadDurationMs(
                    skill,
                    hitFrameCount,
                );
                animations.push(
                    delay(leadDurationMs).then(() => defenderClip.playHit()),
                );
            }
        }
        if (skillCastBanner.value) {
            animations.push(
                skillCastBanner.value.play({
                    side: step.side!,
                    skillName: step.skillName ?? skill.name,
                    attributeName: skill.type.name,
                    attributeIconUrl: skill.getSkillTypeUrl(),
                    criticalHit: step.criticalHit === true,
                })
            );
        }

        if (animations.length > 0) await Promise.all(animations);
        else await delay(SKIP_MS);

        // Legacy players keep their existing post-attack hit timing. SWFClip
        // synchronizes the two timelines above and therefore skips this path.
        if (animationMode.value !== "swfclip" && shouldPlayHit(step) && step.side) {
            const defenderSide = step.side === "left" ? "right" : "left";
            const defenderAnim = animationFor(defenderSide);
            if (defenderAnim && "playHit" in defenderAnim) {
                await defenderAnim.playHit();
            }
        }
        // Ruffle has no tag control here, so leave its existing looping
        // timeline untouched.
    } else if (step.actionType === "switch") {
        await delay(SWITCH_MS);
    } else if (step.actionType === "prop") {
        if (step.propName) {
            stepMessage.value = `【${step.actorName}】使用【${step.propName}】`;
        }
        await delay(SKIP_MS);
    } else {
        await delay(SKIP_MS);
    }
}

watch(
    () => [battle.phase, battle.currentRound] as const,
    ([phase]) => {
        if (phase === "executing") runReplay();
        else {
            replay.cancel();
            stepMessage.value = "";
            skillCastBanner.value?.cancel();
        }
    }
);

function pickedPets() {
    return [
        ...battle.beginning.leftSeer.petPackage,
        ...battle.beginning.rightSeer.petPackage,
    ];
}

function warmSelectedAnimationCache() {
    if (battle.phase === "draft") return;
    if (animationMode.value === "swfclip") {
        void warmSwfClipCache(pickedPets());
    } else {
        void warmSwfCache(pickedPets());
    }
}

function warmDraftResources() {
    if (animationMode.value !== "swfclip") return;
    const { draftPool: pool, draftLeft: left, draftRight: right } = battle;
    retainSwfClipSkins([...pool, ...left, ...right]);
    void warmSwfClipCache([...left.slice(0, 1), ...right.slice(0, 1)], 1);
    void warmSwfClipCache([...left.slice(1), ...right.slice(1)], 2);
    void warmSwfClipCache(pool);
    void warmSwfClipCache([...left, ...right], 4, true);
}

onMounted(() => {
    const frame = requestAnimationFrame(warmDraftResources);
    onBeforeUnmount(() => cancelAnimationFrame(frame));
});
watch(
    () => [battle.draftPool.slice(), battle.draftLeft.slice(), battle.draftRight.slice()] as const,
    warmDraftResources,
    { flush: "post" },
);
onBeforeUnmount(resetSwfClipPreload);

// 草稿结束（进入 selecting）后，缓存当前播放器所需的最终阵容全部皮肤资源。
watch(
    () => battle.phase,
    (phase, previous) => {
        if (phase === "draft") {
            resetSwfPreload();
            if (previous) resetSwfClipPreload();
        } else if (phase === "selecting") {
            warmSelectedAnimationCache();
        }
    },
    { immediate: true },
);

// 对战中切换播放器时，按需补热该模式尚未缓存的全部皮肤。
watch(animationMode, () => {
    // Clear bounds while the selected renderer is loading its first frame.
    leftStageBounds.value = undefined;
    rightStageBounds.value = undefined;
    requestAnimationFrame(updateStageViewportSize);
    if (battle.phase === "draft") warmDraftResources();
    else warmSelectedAnimationCache();
});

watch(
    () => battle.viewMode,
    (mode) => {
        if (mode !== "live") {
            skillCastBanner.value?.cancel();
        }
    }
);

/** 阶段状态文案 */
const phaseText = computed(() => {
    if (battle.viewMode === "review") return "回看模式";
    if (battle.phase === "draft") return "精灵池选择阶段…";
    if (battle.phase === "executing")
        return stepMessage.value || "回合进行中…";
    if (battle.phase === "ended") {
        if (battle.surrenderedSide) {
            const loser =
                battle.surrenderedSide === "left" ? "挑战方" : "被挑战方";
            const winner = battle.winnerSide === "left" ? "挑战方" : "被挑战方";
            return `${loser}认输，${winner}获胜`;
        }
        return battle.winnerSide ? `${battle.winnerSide === "left" ? "挑战方" : "被挑战方"}获胜` : "双方全灭，平局";
    }
    const waitLeftReplace =
        battle.needsReplacement("left") && !battle.getReplacement("left");
    const waitRightReplace =
        battle.needsReplacement("right") && !battle.getReplacement("right");
    if (waitLeftReplace && waitRightReplace) return "请双方选择出战精灵…";
    if (waitLeftReplace) return "等待挑战方选择出战精灵…";
    if (waitRightReplace) return "等待被挑战方选择出战精灵…";
    if (battle.hasSelected("left") && battle.hasSelected("right"))
        return "执行中…";
    if (battle.hasSelected("left")) return "等待被挑战方选择…";
    if (battle.hasSelected("right")) return "等待挑战方选择…";
    return "请双方选择操作";
});
</script>

<template>
    <div
        class="seer-root"
        :class="{ 'shared-stage-mode': sharedStageMode }"
    >
        <div v-if="!portraitHintDismissed" class="portrait-hint">横屏体验更佳<button type="button" @click="portraitHintDismissed = true" aria-label="收起横屏提示">×</button></div>
        <!-- 对战开始前的精灵池 ban & pick -->
        <DraftOverlay />

        <!-- 背景舞台层 -->
        <div
            v-if="battleVisible"
            class="stage"
            :style="{ backgroundColor: sharedStageMode ? stageBackgroundColor : undefined }"
        >
            <div
                ref="stageViewportRef"
                class="stage-camera"
                :class="{ 'shared-stage': sharedStageMode }"
            >
                <div
                    class="swf-wrap"
                    :style="sharedStageMode ? undefined : { transform: `scale(${DEFAULT_BATTLE_STAGE_ZOOM})` }"
                >
                    <SwfAnimation
                        v-if="leftPet && animationMode === 'ruffle'"
                        ref="leftAnim"
                        :pet="leftPet"
                        :transform="false"
                    />
                    <SwfClipAnimation
                        v-if="leftPet && animationMode === 'swfclip'"
                        ref="leftSwfClipAnim"
                        :pet-id="leftPet.当前皮肤.id"
                        :transform="false"
                        battle-side="left"
                        :viewport="stageRenderViewport"
                        @bounds="setStageBounds('left', $event)"
                        @error="clearStageBounds('left')"
                    />
                </div>
                <div
                    class="swf-wrap"
                    :style="sharedStageMode ? undefined : { transform: `scale(${DEFAULT_BATTLE_STAGE_ZOOM})` }"
                >
                    <SwfAnimation
                        v-if="rightPet && animationMode === 'ruffle'"
                        ref="rightAnim"
                        :pet="rightPet"
                        :transform="true"
                    />
                    <SwfClipAnimation
                        v-if="rightPet && animationMode === 'swfclip'"
                        ref="rightSwfClipAnim"
                        :pet-id="rightPet.当前皮肤.id"
                        :transform="true"
                        battle-side="right"
                        :viewport="stageRenderViewport"
                        @bounds="setStageBounds('right', $event)"
                        @error="clearStageBounds('right')"
                    />
                </div>
            </div>
        </div>

        <!-- 前景浮层 -->
        <div v-if="battleVisible" class="ui-layer">
            <!-- 顶部三等分 grid -->
            <div class="top-grid">
                <!-- 左列：精灵信息 -->
                <PetInfo :pet="leftPet" pos="left" />

                <!-- 中列：克制关系 + 状态栏 -->
                <div class="center-col">
                    <span class="round-no" v-if="displayRoundNumber">
                        第 {{ displayRoundNumber }} 回合
                    </span>
                    <div class="restrain-wrap">
                        <Restrain :leftPet="leftPet" :rightPet="rightPet" />
                    </div>
                    <span class="phase">{{ phaseText }}</span>
                    <div class="status-bar">
                        <span
                            v-if="preloadState.status === 'loading'"
                            class="preload-status"
                        >
                            {{ preloadState.label }} 下载缓存中
                            {{ preloadState.finished }}/{{ preloadState.total }}
                        </span>
                        <button
                            v-if="battle.viewMode === 'review'"
                            class="resume-btn"
                            @click="battle.resumeLive()"
                        >
                            返回实时
                        </button>
                    </div>
                    <div class="center-actions">
                        <button type="button" class="center-icon-btn" aria-label="设置" title="设置" :aria-expanded="showSettings" @click="showSettings = !showSettings">
                            <span class="center-icon settings-icon" aria-hidden="true"></span>
                        </button>
                        <button type="button" class="center-icon-btn" aria-label="对战棋谱" title="对战棋谱" :aria-expanded="showBattleInfo" @click="showBattleInfo = true">
                            <span class="center-icon sheet-icon" aria-hidden="true"></span>
                        </button>
                    </div>
                </div>

                <!-- 右列：精灵信息 -->
                <div class="right-cell">
                    <PetInfo :pet="rightPet" pos="right" />
                </div>
            </div>

            <!-- 独立伤害显示行 -->
            <div class="damage-grid">
                <div class="damage-cell left-damage">
                    <Damage
                        :pet="leftPet"
                        :critical-strike="rightPet?.criticalStrike"
                        pos="left"
                    />
                </div>
                <div aria-hidden="true"></div>
                <div class="damage-cell right-damage">
                    <Damage
                        :pet="rightPet"
                        :critical-strike="leftPet?.criticalStrike"
                        pos="right"
                    />
                </div>
            </div>

            <!-- 中部弹性空白 -->
            <div class="spacer"></div>

            <!-- 技能释放提示条 -->
            <SkillCastBanner ref="skillCastBanner" />

            <!-- 底部操作区：左下 + 右下 对称双面板 -->
            <div class="bottom-bar">
                <!-- 挑战方面板（左下） -->
                <div class="panel left-panel">
                    <div class="panel-tabs">
                        <button
                            class="tab-btn"
                            :class="{ active: leftTab === 'skill' }"
                            @click="leftTab = 'skill'"
                        >
                            技能
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: leftTab === 'bag' }"
                            @click="leftTab = 'bag'"
                        >
                            精灵背包
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: leftTab === 'prop' }"
                            @click="leftTab = 'prop'"
                        >
                            道具
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: leftTab === 'choice' }"
                            @click="leftTab = 'choice'"
                        >
                            选择
                        </button>
                    </div>
                    <div class="panel-content">
                        <div
                            v-show="leftTab === 'skill'"
                            class="panel-section"
                        >
                            <SkillGroup :seer="leftSeer" side="left" />
                        </div>
                        <div
                            v-show="leftTab === 'bag'"
                            class="panel-section"
                        >
                            <PetPackage :seer="leftSeer" side="left" />
                        </div>
                        <div v-show="leftTab === 'prop'" class="panel-section">
                            <PropGroup :seer="leftSeer" side="left" />
                        </div>
                        <div v-show="leftTab === 'choice'" class="panel-section">
                            <ChoiceGroup side="left" />
                        </div>
                    </div>
                </div>

                <!-- 被挑战方面板（右下） -->
                <div class="panel right-panel">
                    <div class="panel-tabs">
                        <button
                            class="tab-btn"
                            :class="{ active: rightTab === 'skill' }"
                            @click="rightTab = 'skill'"
                        >
                            技能
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: rightTab === 'bag' }"
                            @click="rightTab = 'bag'"
                        >
                            精灵背包
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: rightTab === 'prop' }"
                            @click="rightTab = 'prop'"
                        >
                            道具
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: rightTab === 'choice' }"
                            @click="rightTab = 'choice'"
                        >
                            选择
                        </button>
                    </div>
                    <div class="panel-content">
                        <div
                            v-show="rightTab === 'skill'"
                            class="panel-section"
                        >
                            <SkillGroup :seer="rightSeer" side="right" />
                        </div>
                        <div
                            v-show="rightTab === 'bag'"
                            class="panel-section"
                        >
                            <PetPackage :seer="rightSeer" side="right" />
                        </div>
                        <div v-show="rightTab === 'prop'" class="panel-section">
                            <PropGroup :seer="rightSeer" side="right" />
                        </div>
                        <div v-show="rightTab === 'choice'" class="panel-section">
                            <ChoiceGroup side="right" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- 棋谱弹出面板 -->
            <BattleInfo v-if="showBattleInfo" @close="showBattleInfo = false" />
        </div>
        <div v-if="showSettings" class="settings-layer" @click.self="showSettings = false">
            <section class="settings-card" aria-label="窗口设置">
                <div class="settings-heading">
                    <span>窗口设置</span>
                    <button type="button" @click="showSettings = false">关闭</button>
                </div>
                <label class="animation-mode">
                    播放器
                    <select v-model="animationMode">
                        <option value="ruffle">Ruffle（Flash）</option>
                        <option value="swfclip">SWFClip（FFDEC + WebGL）</option>
                    </select>
                </label>
                <template v-if="sharedStageMode">
                    <div class="settings-section">
                        <div class="settings-section-title">画布</div>
                        <div class="canvas-settings-row">
                            <button type="button" class="settings-action" @click="() => fitStageToWindow()">
                                适应窗口
                            </button>
                            <label class="color-setting">
                                <span>背景色</span>
                                <input
                                    v-model="stageBackgroundColor"
                                    type="color"
                                    aria-label="选择舞台背景色"
                                />
                                <code>{{ stageBackgroundColor }}</code>
                            </label>
                            <button
                                v-if="stageBackgroundColor !== DEFAULT_STAGE_BACKGROUND_COLOR"
                                type="button"
                                class="settings-action compact"
                                @click="resetStageBackgroundColor"
                            >
                                重置
                            </button>
                        </div>
                    </div>
                    <form class="settings-section viewport-settings" aria-label="视口参数" @submit.prevent="applyStageViewport">
                        <div class="settings-section-title">视口</div>
                        <label>
                            <span>缩放倍率（%）</span>
                            <input
                                v-model="stageZoomInput"
                                type="number"
                                :min="MIN_BATTLE_STAGE_ZOOM * 100"
                                :max="MAX_BATTLE_STAGE_ZOOM * 100"
                                step="0.01"
                                required
                            />
                        </label>
                        <label>
                            <span>竖直偏移（%）</span>
                            <input v-model="stageVerticalOffsetInput" type="number" step="0.1" required />
                        </label>
                        <button type="submit" class="settings-action compact">设置</button>
                    </form>
                    <p class="settings-note">
                        舞台按窗口宽度保持 960×560 比例，竖直溢出部分裁切。默认内容缩放为 80%；竖直偏移以完整舞台高度的百分比计算，默认 +10%，正值向下、负值向上，0% 为居中。缩放通过上方百分比输入调整；“适应窗口”可调整倍率以显示舞台外内容。
                    </p>
                </template>
                <p v-else class="settings-note">
                    Ruffle 按窗口宽度保持 960×560 舞台比例，内容居中缩小至 80%，竖直溢出部分裁切。手动缩放与舞台设置仅适用于 SWFClip。
                </p>
            </section>
        </div>
    </div>
</template>

<style scoped>
.seer-root {
    position: relative;
    width: var(--available-width);
    height: var(--stage-height);
    min-height: 0;
}

/* ===== 背景舞台层 ===== */
.stage {
    position: absolute;
    inset: 0;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #12121f;
}

.stage-camera {
    position: relative;
    flex-shrink: 0;
    width: var(--available-width);
    height: calc(var(--available-width) * 560 / 960);
}

.stage-camera.shared-stage {
    aspect-ratio: 12 / 7;
    overflow: hidden;
    user-select: none;
}

.stage-camera:not(.shared-stage) {
    position: absolute;
    top: 50%;
    left: 50%;
    aspect-ratio: 12 / 7;
    background: #000;
    box-shadow: 0 0 0 calc(var(--ui-unit) * 1) rgba(255, 215, 0, 0.18),
        inset 0 calc(var(--ui-unit) * -40) calc(var(--ui-unit) * 80) rgba(0, 0, 0, 0.55);
    transform: translate(-50%, -50%);
}

.swf-wrap {
    position: absolute;
    inset: 0;
    transform-origin: center;
}

.seer-root.shared-stage-mode .swf-wrap {
    overflow: visible;
    background: transparent;
    pointer-events: none;
}

/* ===== 前景浮层 ===== */
.ui-layer {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    min-height: 0;
    margin: 0 auto;
    padding: calc(var(--ui-unit) * 10) calc(var(--ui-unit) * 10) 0 calc(var(--ui-unit) * 10);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    pointer-events: none;
}

.top-grid,
.damage-grid,
.bottom-bar,
.skill-cast-row,
.settings-layer {
    pointer-events: auto;
}

/* ===== 顶部三等分 grid ===== */
.top-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) calc(var(--ui-unit) * 220) minmax(0, 1fr);
    align-items: start;
    gap: calc(var(--ui-unit) * 8);
}

/* 中列：克制关系 + 状态栏 */
.center-col {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: calc(var(--ui-unit) * 4);
    padding-top: calc(var(--ui-unit) * 4);
}

.center-actions {
    display: flex;
    justify-content: center;
    gap: calc(var(--ui-unit) * 8);
}

.center-icon-btn {
    display: grid;
    place-items: center;
    padding: calc(var(--ui-unit) * 6);
    border: 0;
    background: transparent;
    color: #b9d8ff;
    cursor: pointer;
}

.center-icon-btn:hover {
    color: #fff;
}

.center-icon-btn:focus-visible {
    outline: calc(var(--ui-unit) * 2) solid #ffd700;
    outline-offset: calc(var(--ui-unit) * 2);
}

.center-icon {
    width: calc(var(--ui-unit) * 24);
    height: calc(var(--ui-unit) * 24);
    background: currentColor;
    mask: var(--icon-url) center / contain no-repeat;
}

.settings-icon {
    --icon-url: url('/assets/settings.svg');
}

.sheet-icon {
    --icon-url: url('/assets/sheet.svg');
}

.settings-layer {
    position: fixed;
    inset: 0;
    z-index: 20;
    display: grid;
    place-items: center;
    padding: calc(var(--ui-unit) * 24);
    background: rgba(0, 0, 0, 0.62);
    backdrop-filter: blur(calc(var(--ui-unit) * 4));
}

.settings-card {
    display: flex;
    width: min(calc(var(--ui-unit) * 560), 92%);
    height: auto;
    flex-direction: column;
    overflow: hidden;
    border: calc(var(--ui-unit) * 1) solid rgba(125, 180, 255, 0.35);
    border-radius: calc(var(--ui-unit) * 12);
    background: #080d1a;
    box-shadow: 0 calc(var(--ui-unit) * 16) calc(var(--ui-unit) * 60) rgba(0, 0, 0, 0.55);
}

.settings-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(var(--ui-unit) * 10) calc(var(--ui-unit) * 14);
    color: #d9e6ff;
    background: rgba(15, 27, 52, 0.95);
    font-size: calc(var(--ui-unit) * 13);
    font-weight: 600;
}

.settings-heading button {
    padding: calc(var(--ui-unit) * 3) calc(var(--ui-unit) * 8);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.2);
    border-radius: calc(var(--ui-unit) * 5);
    color: #d9e6ff;
    background: transparent;
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 12);
}

.settings-section {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(var(--ui-unit) * 10);
    padding: calc(var(--ui-unit) * 12) calc(var(--ui-unit) * 14);
    color: #d9e6ff;
    border-top: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.06);
    font-size: calc(var(--ui-unit) * 12);
}

.settings-section-title {
    flex: 0 0 100%;
    color: #98acd0;
    font-size: calc(var(--ui-unit) * 11);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.canvas-settings-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(var(--ui-unit) * 10);
    width: 100%;
}

.settings-action {
    padding: calc(var(--ui-unit) * 5) calc(var(--ui-unit) * 10);
    border: calc(var(--ui-unit) * 1) solid rgba(125, 180, 255, 0.45);
    border-radius: calc(var(--ui-unit) * 5);
    color: #d9e6ff;
    background: rgba(20, 42, 78, 0.75);
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 12);
}

.settings-action:hover {
    border-color: rgba(125, 180, 255, 0.9);
    background: rgba(45, 82, 135, 0.9);
}

.settings-action.compact {
    padding: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 8);
}

.color-setting {
    display: inline-flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 7);
    margin-left: auto;
}

.color-setting input {
    width: calc(var(--ui-unit) * 28);
    height: calc(var(--ui-unit) * 24);
    padding: 0;
    border: calc(var(--ui-unit) * 1) solid rgba(190, 210, 255, 0.35);
    border-radius: calc(var(--ui-unit) * 4);
    background: transparent;
    cursor: pointer;
}

.color-setting code {
    color: #98acd0;
    font-size: calc(var(--ui-unit) * 11);
}

.viewport-settings label {
    display: inline-flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 6);
}

.viewport-settings label span {
    color: #98acd0;
}

.viewport-settings input {
    width: calc(var(--ui-unit) * 84);
    padding: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 6);
    border: calc(var(--ui-unit) * 1) solid rgba(190, 210, 255, 0.35);
    border-radius: calc(var(--ui-unit) * 4);
    color: #d9e6ff;
    background: #0b1426;
    font-size: calc(var(--ui-unit) * 12);
}



.animation-mode {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(var(--ui-unit) * 14);
    padding: calc(var(--ui-unit) * 14);
    color: #d9e6ff;
    font-size: calc(var(--ui-unit) * 13);
}

.animation-mode select {
    min-width: calc(var(--ui-unit) * 210);
    padding: calc(var(--ui-unit) * 5) calc(var(--ui-unit) * 8);
    border: calc(var(--ui-unit) * 1) solid rgba(190, 210, 255, 0.35);
    border-radius: calc(var(--ui-unit) * 5);
    color: #d9e6ff;
    background: #0b1426;
}

.settings-note {
    margin: 0;
    padding: 0 calc(var(--ui-unit) * 14) calc(var(--ui-unit) * 14);
    color: #98acd0;
    font-size: calc(var(--ui-unit) * 12);
}

.restrain-wrap {
    display: flex;
    justify-content: center;
}

/* 右列右对齐 */
.right-cell {
    min-width: 0;
    display: flex;
    justify-content: flex-end;
}

/* ===== 独立伤害显示行 ===== */
.damage-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    min-height: calc(var(--ui-unit) * 64);
    gap: calc(var(--ui-unit) * 8);
}

.damage-cell {
    display: flex;
    align-items: center;
}

.left-damage {
    justify-content: flex-end;
}

.right-damage {
    justify-content: flex-start;
}

/* ===== 状态栏（中列下方） ===== */
.status-bar {
    box-sizing: border-box;
    max-width: 100%;
    flex-wrap: wrap;
    overflow-wrap: anywhere;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(var(--ui-unit) * 10);
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 8);
}

.phase {
    max-width: 100%;
    text-align: center;
    overflow-wrap: anywhere;
    color: gold;
    font-weight: bold;
    font-size: calc(var(--ui-unit) * 13);
    text-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(255, 215, 0, 0.4);
}

.round-no {
    padding: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 16);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 215, 0, 0.45);
    border-radius: calc(var(--ui-unit) * 999);
    background: rgba(20, 25, 40, 0.8);
    color: #ffd700;
    font-size: calc(var(--ui-unit) * 13);
    font-weight: 700;
    text-align: center;
}

.preload-status {
    color: #7db4ff;
    font-size: calc(var(--ui-unit) * 12);
    animation: preload-pulse 1.6s ease-in-out infinite;
}

@keyframes preload-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.45;
    }
}

.resume-btn {
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 10);
    background: rgba(45, 45, 45, 0.9);
    color: #ffd700;
    border: calc(var(--ui-unit) * 1) solid #ffd700;
    border-radius: calc(var(--ui-unit) * 5);
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 11);
    transition: all 0.2s;
}

.resume-btn:hover {
    background: #ffd700;
    color: #1a1a1a;
}

/* 中部弹性空白 */
.spacer {
    flex: 1 1 auto;
    min-height: calc(var(--ui-unit) * 12);
}

/* ===== 底部双面板 ===== */
.bottom-bar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: calc(var(--ui-unit) * 16);
    padding: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 10) calc(var(--ui-unit) * 10) calc(var(--ui-unit) * 10);
}

.panel {
    min-width: 0;
    max-width: calc(50% - var(--ui-unit) * 8);
    display: flex;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 6);
}

.left-panel {
    align-items: flex-start;
}

.right-panel {
    align-items: flex-end;
}

.panel-tabs {
    display: flex;
    gap: calc(var(--ui-unit) * 2);
}

.tab-btn {
    padding: calc(var(--ui-unit) * 5) calc(var(--ui-unit) * 12);
    background: rgba(20, 20, 35, 0.85);
    color: #a0a0b0;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-bottom: calc(var(--ui-unit) * 2) solid transparent;
    border-radius: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 6) 0 0;
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 13);
    font-weight: 600;
    transition: all 0.2s;
    backdrop-filter: blur(calc(var(--ui-unit) * 8));
}

.tab-btn:hover {
    color: #e0e0e0;
    background: rgba(30, 30, 50, 0.9);
}

.tab-btn.active {
    color: #ffd700;
    border-bottom-color: #ffd700;
    background: rgba(30, 30, 50, 0.95);
    box-shadow: 0 0 calc(var(--ui-unit) * 12) rgba(255, 215, 0, 0.15);
}

.panel-content {
    box-sizing: border-box;
    max-width: 100%;
    background: rgba(12, 12, 24, 0.82);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.06);
    border-radius: calc(var(--ui-unit) * 8);
    padding: calc(var(--ui-unit) * 8);
    backdrop-filter: blur(calc(var(--ui-unit) * 12));
    min-height: calc(var(--ui-unit) * 60);
}

.panel-section {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 6);
}

.portrait-hint { display: none; }



.settings-layer { position: absolute; padding: calc(var(--ui-unit) * 24); }
.settings-card { width: calc(var(--ui-unit) * 560); max-height: 100%; overflow-y: auto; }
</style>
